import { ItemVM } from "@cmodules/checklist/assets/data/types"
import { AnyItemVM } from "@cmodules/checklist/assets/data/types/vm/template-builder/item"

/**
 * A rule the user should have a look at after the option uuids of a list item were regenerated,
 * see utility/response-options/option-uuid-refresh.
 */
export type AffectedRuleEntry = {
  key: string;
  kind: 'rule_set' | 'simple_rule';
  itemId: string;
  itemTitle: string;
  sourceItem?: { id: string; title: string } | null;
  ruleSetId: string | null;
  /** the rule set could not be re-pointed automatically, the user has to check it by hand */
  manualReview: boolean;
}

/**
 * Review list shown after the option uuids of a list item were regenerated, see
 * utility/response-options/option-uuid-refresh. Purely informative, the user only navigates
 * through the rules that referenced the renamed options to check them.
 */
export type ChangedOptionsRulesStore = {
  show: boolean;
  key: number;
  sourceItem: { id: string; title: string } | null;
  entries: AffectedRuleEntry[];
}

export const EMPTY_CHANGED_OPTIONS_RULES: ChangedOptionsRulesStore = {
  show: false,
  key: 0,
  sourceItem: null,
  entries: []
}

/**
 * A rule referenced by a changed option, grouped with the question that owns it so the wizard can
 * show the user exactly what would be touched before the rules are auto-updated, see
 * utility/response-options/option-uuid-refresh.
 */
export type PendingRuleUpdateEntry = {
  /** rule set uuid, `null` for the item's own simple rules */
  ruleSetId: string | null;
  /** id of the item owning the rule / simple rule */
  itemId: string;
  /** title of the item owning the rule / simple rule */
  itemTitle: string;
  /** whether the rule is a full rule set or the item's own simple rules */
  kind: 'rule_set' | 'simple_rule';
}

/**
 * One changed option shown at the top of the wizard, with the labels before / after the change.
 */
export type ChangedOptionLabelEntry = {
  /** option uuid after the change */
  optionUuid: string;
  /** label before the change */
  oldLabel: string;
  /** label after the change */
  newLabel: string;
}

/**
 * One editable translation for the wizard's first step (language-tab-based), see
 * utility/response-options/option-uuid-refresh.
 */
export type WizardTranslationEntry = {
  optionUuid: string;
  label: string;
  lang: string;
  value: string;
}

/**
 * Pending confirmation shown before the rules referencing a changed option are auto-updated.
 * Rendered with the ready-made `@components/wizard` (bs-stepper). It has up to two steps:
 *  1. translations — language-tab-based inputs for the changed labels, shown only when at least one
 *     translation exists; the edited drafts are saved when the user advances to the next step;
 *  2. rules — the related rules with an auto / manual choice, suggesting the automatic re-pointing
 *     when the relabel is logically neutral.
 * The auto / manual choice only concerns the rules; translations are always saved when leaving
 * step 1, never attached to the rule action.
 */
export type ConfirmRuleUpdateStore = {
  show: boolean;
  key: number;
  /** template id used to persist the edited translations */
  templateId: string | null;
  /** the item whose options were changed */
  sourceItem: { id: string; title: string } | null;
  /** whether option uuids were actually regenerated/changed */
  hasUuidChanges?: boolean;
  /** the changed options (old / new label), shown at the top of the wizard */
  changedOptions: ChangedOptionLabelEntry[];
  /** existing translations of the changed options, edited in step 1 */
  translations: WizardTranslationEntry[];
  /** every rule / simple rule that references one of the changed options, handled in step 2 */
  entries: PendingRuleUpdateEntry[];
  /** invoked when the user opts for the automatic rule update */
  onConfirm: (() => void) | null;
  /** invoked when the user opts for a manual rule fix */
  onManualFix: (() => void) | null;
}

export const EMPTY_CONFIRM_RULE_UPDATE: ConfirmRuleUpdateStore = {
  show: false,
  key: 0,
  templateId: null,
  sourceItem: null,
  hasUuidChanges: false,
  changedOptions: [],
  translations: [],
  entries: [],
  onConfirm: null,
  onManualFix: null
}

class ModalsStore {
  conflictModal: {
    show: boolean;
    msg: string;
  };
  groupOptionsModal: {
    show: boolean;
    item: AnyItemVM;
    flatData: {
      uuid: string;
      type: string;
      options: Record<string, any>;
      items: any[];
    };
  };
  relatedInfoModal: {
    show: boolean;
    item: AnyItemVM;
  };
  imageMarkerModal: {
    show: boolean;
    item: AnyItemVM;
  };
  rulesModal: {
    show: boolean;
    item: AnyItemVM;
  };
  repeatingGroupModal: {
    show: boolean;
    item: AnyItemVM;
  };
  pageDndModalShow: boolean;
  conflictedRules: {
    selectedItems: AnyItemVM[];
    showModal: boolean;
    key: number;
    action: {
      type: string | null;
      result: any | null;
      activeItems: AnyItemVM[];
      callback: () => void;
    };
  };
  changedOptionsRules: ChangedOptionsRulesStore;
  confirmRuleUpdate: ConfirmRuleUpdateStore;

  constructor(
    conflictModal: {
      show: boolean;
      msg: string;
    } = { show: false, msg: '' },
    groupOptionsModal: {
      show: boolean;
      item: AnyItemVM;
      flatData: {
        uuid: string;
        type: string;
        options: Record<string, any>;
        items: any[];
      };
    } = {
        show: false,
        item: ItemVM.generateEmptyObjectAsAnyItemVM(),
        flatData: {
          uuid: '',
          type: '',
          options: {},
          items: []
        }
      },
    relatedInfoModal: {
      show: boolean;
      item: AnyItemVM;
    } = { show: false, item: ItemVM.generateEmptyObjectAsAnyItemVM() },
    imageMarkerModal: {
      show: boolean;
      item: AnyItemVM;
    } = { show: false, item: ItemVM.generateEmptyObjectAsAnyItemVM() },
    rulesModal: {
      show: boolean;
      item: AnyItemVM;
    } = { show: false, item: ItemVM.generateEmptyObjectAsAnyItemVM() },
    repeatingGroupModal: {
      show: boolean;
      item: AnyItemVM;
    } = { show: false, item: ItemVM.generateEmptyObjectAsAnyItemVM() },
    pageDndModalShow = false,
    conflictedRules: {
      selectedItems: any[];
      showModal: boolean;
      key: number;
      action: {
        type: string | null;
        result: any | null;
        activeItems: any[];
        callback: () => void;
      };
    } = {
        selectedItems: [],
        showModal: false,
        key: 0,
        action: {
          type: null,
          result: null,
          activeItems: [],
          callback: () => { }
        }
      },
    changedOptionsRules: ChangedOptionsRulesStore = { ...EMPTY_CHANGED_OPTIONS_RULES },
    confirmRuleUpdate: ConfirmRuleUpdateStore = { ...EMPTY_CONFIRM_RULE_UPDATE }
  ) {
    this.conflictModal = conflictModal
    this.groupOptionsModal = groupOptionsModal
    this.relatedInfoModal = relatedInfoModal
    this.imageMarkerModal = imageMarkerModal
    this.rulesModal = rulesModal
    this.repeatingGroupModal = repeatingGroupModal
    this.pageDndModalShow = pageDndModalShow
    this.conflictedRules = conflictedRules
    this.changedOptionsRules = changedOptionsRules
    this.confirmRuleUpdate = confirmRuleUpdate
  }
}

export { ModalsStore }
